import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { BRAND_NAME } from '@/config/nav';
import { AuthShell } from '../components/AuthShell';
import type { ApiError } from '@/core/api';
import { confirmPasswordReset } from '../api';

const schema = z
  .object({
    new_password1: z.string().min(8, 'Use at least 8 characters'),
    new_password2: z.string().min(1, 'Confirm your password'),
  })
  .refine((v) => v.new_password1 === v.new_password2, {
    path: ['new_password2'],
    message: 'Passwords do not match',
  });

type FormValues = z.infer<typeof schema>;
const FIELD_KEYS = ['new_password1', 'new_password2'] as const;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const uid = params.get('uid') ?? '';
  const token = params.get('token') ?? '';
  const [done, setDone] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { new_password1: '', new_password2: '' },
  });

  async function onSubmit(values: FormValues) {
    try {
      await confirmPasswordReset({ uid, token, ...values });
      setDone(true);
    } catch (e) {
      const err = e as ApiError;
      const fe = err?.fieldErrors;
      if (fe) {
        for (const [field, messages] of Object.entries(fe)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
      // An invalid/expired uid or token surfaces here — keep it user-friendly.
      if (!fe || fe.uid || fe.token) {
        form.setError('root', {
          message:
            'This link is invalid or has expired. Ask an admin to resend your invite.',
        });
      }
    }
  }

  const submitting = form.formState.isSubmitting;
  const linkMissing = !uid || !token;

  return (
    <AuthShell>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Set your password</CardTitle>
          <CardDescription>
            {done
              ? `You're all set — sign in to ${BRAND_NAME}.`
              : `Choose a password for your ${BRAND_NAME} account.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <Button asChild className="w-full">
              <Link to="/login">Continue to sign in</Link>
            </Button>
          ) : linkMissing ? (
            <div className="space-y-4">
              <p className="text-destructive text-sm">
                This link is missing its security token. Open the most recent invite
                email, or ask an admin to resend it.
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/login">Back to sign in</Link>
              </Button>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="new_password1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New password</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="new_password2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm password</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="new-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {form.formState.errors.root ? (
                  <p className="text-destructive text-sm">
                    {form.formState.errors.root.message}
                  </p>
                ) : null}
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? 'Saving…' : 'Set password'}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  );
}
